/**
 * ROAM rentals catalogue.
 *
 * This is the single source of truth for the /rentals flow.
 * Add a new listing by pushing another object into LISTINGS.
 *
 * Media
 * -----
 * • hero:         primary hero photo URL
 * • gallery:      array of additional photo URLs (4-8 recommended)
 * • floorPlan:    optional URL to a floor-plan image
 * • communityVideoUrl: YouTube OR Vimeo URL of the neighbourhood walk-through
 * • propertyVideoUrl:  YouTube OR Vimeo URL of the property tour
 *
 * Status values: "available" | "coming-soon" | "leased"
 */

const IMG = {
  // interiors
  int1: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  int2: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  int3: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  int4: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  int5: "https://images.unsplash.com/photo-1600121848594-d8644e57abab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  int6: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  int7: "https://images.unsplash.com/photo-1567767292278-a4f21aa2d36e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  int8: "https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  town1: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  town2: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  town3: "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  town4: "https://images.unsplash.com/photo-1613977257363-707ba9348227?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // interiors — kitchen / bed / bath / living
  kitchen1: "https://images.unsplash.com/photo-1600489000022-c2086d79f9d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  bed1: "https://images.unsplash.com/photo-1522771739844-6a9f6d5f14af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  bath1: "https://images.unsplash.com/photo-1507652313519-d4e9174996dd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  living1: "https://images.unsplash.com/photo-1560185007-cde436f6a4d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  living2: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
};

// A placeholder demo video for both community + property until you send real links.
// (Any YouTube or Vimeo URL works — the embed component parses either.)
const DEMO_COMMUNITY = "https://www.youtube.com/watch?v=DWcJFNfaw9c";
const DEMO_PROPERTY = "https://www.youtube.com/watch?v=1La4QzGeaaQ";

export const CITIES = [
  {
    slug: "calgary",
    name: "Calgary",
    province: "Alberta",
    tagline: "Where the foothills meet the fold.",
    intro:
      "Four neighbourhoods, one river, and every kind of home from a walk-up above a bakery to a townhome by the Bird Sanctuary.",
    hero: "https://images.unsplash.com/photo-1587547243081-6d1c04dbbbe6?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  },
  {
    slug: "edmonton",
    name: "Edmonton",
    province: "Alberta",
    tagline: "River valley to river valley.",
    intro:
      "New for 2026: a short list of homes in Old Strathcona, Glenora, and Oliver — walk-to-everything places for people who like their weekends slow.",
    hero: "https://images.unsplash.com/photo-1610134253150-a09b90d5b3a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  },
];

export const PROPERTY_TYPES = [
  {
    slug: "apartments",
    name: "Apartments",
    tagline: "Walk-ups, low-rises, and one-of-a-kind lofts.",
  },
  {
    slug: "townhomes",
    name: "Townhomes",
    tagline: "Two & three storeys — usually with a yard.",
  },
];

export const LISTINGS = [
  /* -------------------- CALGARY · APARTMENTS -------------------- */
  {
    slug: "the-larchmont-301",
    name: "The Larchmont",
    unit: "#301",
    city: "calgary",
    type: "apartments",
    neighbourhood: "Mission",
    address: "2110 - 4 Street SW, Calgary, AB T2S 1W8",
    map: { lat: 51.0328, lng: -114.0716 },
    status: "available",
    availableDate: "August 1, 2026",
    monthlyRent: 2450,
    deposit: 2450,
    leaseTerm: "12 months",
    utilities: ["Water", "Heat"],
    specs: {
      beds: 2,
      baths: 1,
      sqft: 880,
      parking: "1 titled underground",
      pets: "Cats & small dogs, on request",
      furnished: false,
    },
    amenities: [
      "In-suite laundry",
      "Gas range",
      "Corner suite with south light",
      "Private balcony",
      "Secure bike room",
      "Two-minute walk to Elbow River pathway",
    ],
    description: [
      "A corner two-bedroom on the quieter side of Mission — south light across the floors, the river a two-minute walk down 4th.",
      "Concrete, oak, and a kitchen you'll actually cook in. Quiet building, twelve suites, no shared corridors to speak of.",
    ],
    hero: IMG.int1,
    gallery: [IMG.kitchen1, IMG.bed1, IMG.bath1, IMG.living1],
    floorPlan: null,
    communityVideoUrl: DEMO_COMMUNITY,
    propertyVideoUrl: DEMO_PROPERTY,
  },
  {
    slug: "the-bridgeland-204",
    name: "The Bridgeland",
    unit: "#204",
    city: "calgary",
    type: "apartments",
    neighbourhood: "Bridgeland",
    address: "832 - 1st Avenue NE, Calgary, AB T2E 0C3",
    map: { lat: 51.0503, lng: -114.0512 },
    status: "available",
    availableDate: "July 15, 2026",
    monthlyRent: 2100,
    deposit: 2100,
    leaseTerm: "12 months",
    utilities: ["Water", "Heat"],
    specs: {
      beds: 2,
      baths: 1,
      sqft: 760,
      parking: "1 stall (surface)",
      pets: "Cats & dogs welcome",
      furnished: false,
    },
    amenities: [
      "In-suite laundry",
      "Balcony over 1st Avenue",
      "Above a bakery — literally",
      "Storage locker",
      "Skyline views",
    ],
    description: [
      "Wake to the smell of the bakery below and the skyline over 1st Avenue. This one's for people who like to walk everywhere.",
      "Two proper bedrooms, in-suite laundry, and a balcony made for slow mornings.",
    ],
    hero: IMG.int2,
    gallery: [IMG.living2, IMG.bed1, IMG.kitchen1, IMG.bath1],
    floorPlan: null,
    communityVideoUrl: DEMO_COMMUNITY,
    propertyVideoUrl: DEMO_PROPERTY,
  },
  {
    slug: "the-kensington-lofts-506",
    name: "The Kensington Lofts",
    unit: "#506",
    city: "calgary",
    type: "apartments",
    neighbourhood: "Kensington",
    address: "112 - 10 Street NW, Calgary, AB T2N 1V3",
    map: { lat: 51.0525, lng: -114.0879 },
    status: "coming-soon",
    availableDate: "September 1, 2026",
    monthlyRent: 1725,
    deposit: 1725,
    leaseTerm: "12 months",
    utilities: ["Water"],
    specs: {
      beds: 1,
      baths: 1,
      sqft: 580,
      parking: "None — you won't need it",
      pets: "Cats & small dogs",
      furnished: false,
    },
    amenities: [
      "Steps to the C-Train",
      "Pet-friendly",
      "Communal laundry down the hall",
      "Café on the ground floor",
    ],
    description: [
      "Downtown in one stop, bookshops at your door. A tidy one-bedroom for someone who'd rather take the train than drive.",
      "Pet-friendly, laundry down the hall, and a café below that knows your order by week two.",
    ],
    hero: IMG.int4,
    gallery: [IMG.living1, IMG.kitchen1, IMG.bed1, IMG.bath1],
    floorPlan: null,
    communityVideoUrl: DEMO_COMMUNITY,
    propertyVideoUrl: DEMO_PROPERTY,
  },
  {
    slug: "the-marda-102",
    name: "The Marda",
    unit: "#102",
    city: "calgary",
    type: "apartments",
    neighbourhood: "Marda Loop",
    address: "2016 - 33 Avenue SW, Calgary, AB T2T 1Z6",
    map: { lat: 51.0207, lng: -114.0965 },
    status: "leased",
    availableDate: "—",
    monthlyRent: 1850,
    deposit: 1850,
    leaseTerm: "12 months",
    utilities: ["Water", "Heat"],
    specs: {
      beds: 1,
      baths: 1,
      sqft: 610,
      parking: "Street permit",
      pets: "On request",
      furnished: false,
    },
    amenities: [
      "Steps to 33rd Ave shops",
      "Big windows, quiet side of the block",
      "In-suite laundry",
    ],
    description: [
      "A bright one-bedroom on the quiet side of the Loop — clean lines, big windows, and the 33rd Ave shops at the end of the block.",
      "Small on square footage, generous on light. The kind of place that stays tidy on its own.",
    ],
    hero: IMG.int3,
    gallery: [IMG.living2, IMG.kitchen1, IMG.bed1, IMG.bath1],
    floorPlan: null,
    communityVideoUrl: DEMO_COMMUNITY,
    propertyVideoUrl: DEMO_PROPERTY,
  },

  /* -------------------- CALGARY · TOWNHOMES -------------------- */
  {
    slug: "the-inglewood-townhome-b",
    name: "The Inglewood Townhome",
    unit: "Unit B",
    city: "calgary",
    type: "townhomes",
    neighbourhood: "Inglewood",
    address: "1114 - 9 Avenue SE, Calgary, AB T2G 0S8",
    map: { lat: 51.0396, lng: -114.032 },
    status: "available",
    availableDate: "August 15, 2026",
    monthlyRent: 2950,
    deposit: 2950,
    leaseTerm: "12 months",
    utilities: [],
    specs: {
      beds: 3,
      baths: 2.5,
      sqft: 1340,
      parking: "2 — garage + driveway",
      pets: "Yes, fenced yard",
      furnished: false,
    },
    amenities: [
      "Three storeys",
      "Attached garage",
      "Fenced back yard",
      "Ridge view from the primary",
      "Walk to Recordland, vinyl shops, coffee",
    ],
    description: [
      "A three-storey townhome on a leafy street — garage below, living in the middle, bedrooms up top with the ridge in the window.",
      "A fenced yard for the dog, a walk to the vinyl shops, and the Bird Sanctuary trails a few minutes on.",
    ],
    hero: IMG.town1,
    gallery: [IMG.town2, IMG.kitchen1, IMG.living1, IMG.bed1, IMG.bath1],
    floorPlan: null,
    communityVideoUrl: DEMO_COMMUNITY,
    propertyVideoUrl: DEMO_PROPERTY,
  },
  {
    slug: "the-hillhurst-townhome-2",
    name: "The Hillhurst Row",
    unit: "Unit 2",
    city: "calgary",
    type: "townhomes",
    neighbourhood: "Hillhurst",
    address: "402 - 10 Street NW, Calgary, AB T2N 1V6",
    map: { lat: 51.054, lng: -114.088 },
    status: "coming-soon",
    availableDate: "October 1, 2026",
    monthlyRent: 3200,
    deposit: 3200,
    leaseTerm: "12 months",
    utilities: [],
    specs: {
      beds: 3,
      baths: 2.5,
      sqft: 1420,
      parking: "1 titled underground",
      pets: "Cats & dogs",
      furnished: false,
    },
    amenities: [
      "Off Kensington Road",
      "Riley Park across the way",
      "In-suite laundry",
      "Rooftop patio",
    ],
    description: [
      "A row-house on the north bank — Kensington Road at one end, Riley Park across the way. Three bedrooms and a rooftop patio you'll actually use.",
      "Made by Marcus is at the end of the block. You've been warned.",
    ],
    hero: IMG.town2,
    gallery: [IMG.town1, IMG.kitchen1, IMG.living2, IMG.bed1, IMG.bath1],
    floorPlan: null,
    communityVideoUrl: DEMO_COMMUNITY,
    propertyVideoUrl: DEMO_PROPERTY,
  },

  /* -------------------- EDMONTON · APARTMENTS -------------------- */
  {
    slug: "the-strathcona-408",
    name: "The Strathcona",
    unit: "#408",
    city: "edmonton",
    type: "apartments",
    neighbourhood: "Old Strathcona",
    address: "10345 - 82 Avenue NW, Edmonton, AB T6E 1Z9",
    map: { lat: 53.5188, lng: -113.501 },
    status: "available",
    availableDate: "August 1, 2026",
    monthlyRent: 1850,
    deposit: 1850,
    leaseTerm: "12 months",
    utilities: ["Water", "Heat"],
    specs: {
      beds: 1,
      baths: 1,
      sqft: 640,
      parking: "1 titled underground",
      pets: "Cats & small dogs",
      furnished: false,
    },
    amenities: [
      "Two blocks to Whyte Ave",
      "Big south-facing windows",
      "In-suite laundry",
      "Bike storage",
    ],
    description: [
      "A one-bedroom above the noise on Whyte — quiet in the back, market view out the front. The Farmer's Market is a Saturday habit worth acquiring.",
      "Old Strathcona's best coffee is closer than your bathroom.",
    ],
    hero: IMG.int5,
    gallery: [IMG.living2, IMG.kitchen1, IMG.bed1, IMG.bath1],
    floorPlan: null,
    communityVideoUrl: DEMO_COMMUNITY,
    propertyVideoUrl: DEMO_PROPERTY,
  },
  {
    slug: "the-oliver-712",
    name: "The Oliver",
    unit: "#712",
    city: "edmonton",
    type: "apartments",
    neighbourhood: "Oliver",
    address: "11710 - 100 Avenue NW, Edmonton, AB T5K 2G3",
    map: { lat: 53.5462, lng: -113.5325 },
    status: "available",
    availableDate: "July 20, 2026",
    monthlyRent: 2100,
    deposit: 2100,
    leaseTerm: "12 months",
    utilities: ["Water", "Heat"],
    specs: {
      beds: 2,
      baths: 2,
      sqft: 820,
      parking: "1 titled underground",
      pets: "On request",
      furnished: false,
    },
    amenities: [
      "River valley view",
      "Two full baths",
      "In-suite laundry",
      "Gym on the third floor",
      "5 min walk to Grant MacEwan",
    ],
    description: [
      "A two-bed, two-bath tower suite with the river valley in the west window — sunset over the trees, downtown behind you.",
      "For couples, roommates, or the work-from-home person who wants a real second bedroom.",
    ],
    hero: IMG.int6,
    gallery: [IMG.living1, IMG.kitchen1, IMG.bed1, IMG.bath1],
    floorPlan: null,
    communityVideoUrl: DEMO_COMMUNITY,
    propertyVideoUrl: DEMO_PROPERTY,
  },

  /* -------------------- EDMONTON · TOWNHOMES -------------------- */
  {
    slug: "the-glenora-townhome-c",
    name: "The Glenora",
    unit: "Unit C",
    city: "edmonton",
    type: "townhomes",
    neighbourhood: "Glenora",
    address: "10420 - 133 Street NW, Edmonton, AB T5N 2A7",
    map: { lat: 53.5453, lng: -113.5567 },
    status: "coming-soon",
    availableDate: "September 15, 2026",
    monthlyRent: 2650,
    deposit: 2650,
    leaseTerm: "12 months",
    utilities: [],
    specs: {
      beds: 3,
      baths: 2.5,
      sqft: 1520,
      parking: "Attached garage + driveway",
      pets: "Cats & dogs, fenced yard",
      furnished: false,
    },
    amenities: [
      "Tree-lined street",
      "Attached garage",
      "Fenced yard",
      "Steps to the ravine trail",
      "Three bedrooms up",
    ],
    description: [
      "Glenora's old-money quiet meets a fresh townhome build — three storeys, an attached garage, and the ravine trail at the end of the block.",
      "The kind of place people never leave.",
    ],
    hero: IMG.town3,
    gallery: [IMG.town4, IMG.kitchen1, IMG.living2, IMG.bed1, IMG.bath1],
    floorPlan: null,
    communityVideoUrl: DEMO_COMMUNITY,
    propertyVideoUrl: DEMO_PROPERTY,
  },
];

/* --------- helper queries --------- */

export const getCity = (citySlug) =>
  CITIES.find((c) => c.slug === citySlug) || null;

export const getType = (typeSlug) =>
  PROPERTY_TYPES.find((t) => t.slug === typeSlug) || null;

export const getListing = (slug) =>
  LISTINGS.find((l) => l.slug === slug) || null;

export const listingsBy = ({ city, type } = {}) => {
  return LISTINGS.filter((l) => {
    if (city && l.city !== city) return false;
    if (type && l.type !== type) return false;
    return true;
  });
};

export const countBy = ({ city, type } = {}) => listingsBy({ city, type }).length;

export const availableCountBy = ({ city, type } = {}) =>
  listingsBy({ city, type }).filter((l) => l.status === "available").length;

export const STATUS_LABEL = {
  available: { label: "Available now", color: "#24382E", bg: "#DDE7CE" },
  "coming-soon": { label: "Coming soon", color: "#5C3D0C", bg: "#F3E4C6" },
  leased: { label: "Leased", color: "#4A4640", bg: "#E4DFD3" },
};
